import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LIBELLES_LANGUE_CHARTE, lireLienRapport, type LienRapport, type RapportResume } from '@rh/shared';
import { appel } from '../api';
import { Chargement, Erreur } from '../composants/Etat';
import { SectionPhotos } from '../composants/SectionPhotos';
import { dateHeure } from '../format';
import { aujourdhui, usePhotosScan } from '../photos-scan';

/**
 * Scan du rapport de référence signé (docs/ARCHITECTURE.md § 5.5) : photos,
 * rattachement par QR code, date de signature. Le dossier passe en « rapport
 * remis ».
 */
export function ScanRapport() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const photos = usePhotosScan();
  const [rapports, setRapports] = useState<RapportResume[] | null>(null);
  const [rapportId, setRapportId] = useState('');
  const [signeLe, setSigneLe] = useState(aujourdhui());
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    appel<RapportResume[]>(`/candidatures/${id}/rapports`)
      .then((liste) => {
        setRapports(liste);
        const enAttente = liste.filter((r) => !r.documentScanId);
        if (enAttente.length === 1) setRapportId((actuel) => actuel || enAttente[0]!.id);
      })
      .catch((e: Error) => setErreur(e.message));
  }, [id]);

  if (!rapports && !erreur) return <Chargement quoi="des rapports" />;
  const enAttente = rapports?.filter((r) => !r.documentScanId) ?? [];

  async function ajouter(fichiers: FileList | null): Promise<void> {
    setErreur(null);
    setInfo(null);
    const textes = await photos.ajouter(fichiers);
    const lien = textes.map(lireLienRapport).find((l): l is LienRapport => l !== null);
    if (!lien) return;
    if (lien.candidatureId !== id) {
      setErreur('Ce QR code appartient au rapport d’un autre candidat : vérifier le document photographié.');
    } else if (!enAttente.some((r) => r.id === lien.rapportId)) {
      setErreur('Ce QR code désigne un rapport déjà scanné ou inconnu pour ce candidat.');
    } else {
      setRapportId(lien.rapportId);
      setInfo('Rapport reconnu par son QR code.');
    }
  }

  async function enregistrer(): Promise<void> {
    if (photos.floues > 0 && !window.confirm(`${photos.floues} photo(s) semblent floues. Enregistrer quand même ?`)) {
      return;
    }
    setErreur(null);
    setEnvoi(true);
    try {
      await appel<RapportResume[]>(`/candidatures/${id}/rapport/signe`, {
        method: 'POST',
        body: photos.formulaire({ rapportId, signeLe }),
      });
      navigate(`/candidatures/${id}`);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <article className="scan-charte">
      <div className="entete-annonce">
        <div>
          <h2>Rapport de référence signé</h2>
          <p className="meta">
            <Link to={`/candidatures/${id}`}>← Fiche</Link>
          </p>
        </div>
      </div>

      {rapports && enAttente.length === 0 && (
        <p className="signal">
          Aucun rapport en attente de signature pour ce candidat : éditer d’abord le rapport depuis sa fiche.
        </p>
      )}

      <SectionPhotos
        photos={photos}
        consigne="Une photo par page, à plat, bien éclairée. Le QR code en haut de la première page rattache le scan."
        onAjout={(fichiers) => void ajouter(fichiers)}
      />

      <section className="bloc">
        <h3>2. Rapport</h3>
        {enAttente.length > 0 ? (
          <select value={rapportId} aria-label="Rapport signé" onChange={(e) => setRapportId(e.target.value)}>
            <option value="">Choisir le rapport photographié…</option>
            {enAttente.map((rapport) => (
              <option key={rapport.id} value={rapport.id}>
                {rapport.id.slice(0, 8).toUpperCase()} — édité le {dateHeure(rapport.editeLe)} —{' '}
                {LIBELLES_LANGUE_CHARTE[rapport.langue]}
              </option>
            ))}
          </select>
        ) : (
          <p className="mention">—</p>
        )}
        <label className="champ">
          Signé le
          <input type="date" value={signeLe} max={aujourdhui()} onChange={(e) => setSigneLe(e.target.value)} />
        </label>
      </section>

      {erreur && <Erreur message={erreur} />}
      {info && <p className="signal">{info}</p>}
      <div className="actions">
        <button
          type="button"
          className="bouton"
          disabled={envoi || photos.traitement || photos.pages.length === 0 || !rapportId}
          onClick={() => void enregistrer()}
        >
          {envoi ? 'Enregistrement…' : 'Enregistrer le rapport signé'}
        </button>
      </div>
    </article>
  );
}
